import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { User as UserOutput, UserDataInput } from "@/schemas/user";
import { getDefaultUserData } from "./userDataUtils";
// import { userSettingsLockState } from "./updateTracking"; // Likely obsolete
import { useFirebase } from "@/context/FirebaseProvider";
import { useUserOperations } from "@/context/schema/useUserOperations";
import { User as FirebaseUser } from "firebase/auth"; // For type annotation

/**
 * Core implementation of the user settings hook, refactored to use FirebaseProvider.
 */
export const useUserSettingsCore = () => {
  const { toast } = useToast();
  const { 
    userData: liveUserData, 
    firebaseUser, 
    isLoading: isFirebaseLoading 
  } = useFirebase();
  
  const { updateUser } = useUserOperations();

  const defaultUserDataVal = useMemo(() => getDefaultUserData(firebaseUser), [firebaseUser]);

  const [formData, setFormData] = useState<UserDataInput>(() => {
    if (liveUserData) {
      const { id, ...dataInput } = liveUserData;
      return dataInput as UserDataInput;
    }
    return defaultUserDataVal;
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const previousUserDataRef = useRef<string>(JSON.stringify(formData));
  const isInitialLoadDoneRef = useRef(false); // Tracks if initial effect has run

  useEffect(() => {
    if (isFirebaseLoading) {
      return; 
    }

    // This effect runs when loading is complete OR liveUserData/defaultUserDataVal changes
    // It ensures formData is correctly initialized or reset when not editing.
    if (!isEditing) {
      if (liveUserData) {
        console.log("useUserSettingsCore: Syncing formData with liveUserData (not editing)");
        const { id, ...dataInput } = liveUserData;
        const newFormData = dataInput as UserDataInput;
        setFormData(newFormData);
        previousUserDataRef.current = JSON.stringify(newFormData);
      } else {
        console.log("useUserSettingsCore: No liveUserData, setting formData to defaults (not editing)");
        setFormData(defaultUserDataVal);
        previousUserDataRef.current = JSON.stringify(defaultUserDataVal);
      }
    }
    isInitialLoadDoneRef.current = true; // Mark initial load as done after first run post-loading
  }, [liveUserData, isEditing, isFirebaseLoading, defaultUserDataVal]);

  // This effect is specifically for initializing formData when editing starts
  useEffect(() => {
    if (isEditing && isInitialLoadDoneRef.current) { // Only run if editing and initial load is done
      console.log("useUserSettingsCore: Editing started, initializing formData for edit.");
      if (liveUserData) {
        const { id, ...dataInput } = liveUserData;
        setFormData(dataInput as UserDataInput);
        previousUserDataRef.current = JSON.stringify(dataInput as UserDataInput);
      } else {
        setFormData(defaultUserDataVal);
        previousUserDataRef.current = JSON.stringify(defaultUserDataVal);
      }
    }
  }, [isEditing, liveUserData, defaultUserDataVal]); // Removed isInitialLoadDoneRef from here, use it in condition

  const saveChanges = useCallback(async () => {
    if (!firebaseUser?.uid) {
      toast({ title: "Error", description: "User not authenticated.", variant: "destructive" });
      return;
    }
    if (!formData) {
      toast({ title: "Error", description: "No data to save.", variant: "destructive" });
      return;
    }

    console.log("useUserSettingsCore: Saving user data:", formData);
    try {
      await updateUser(firebaseUser.uid, formData); 
      previousUserDataRef.current = JSON.stringify(formData);
      toast({ title: "Success", description: "Settings saved successfully." });
      setIsEditing(false);
    } catch (error) {
      console.error("useUserSettingsCore: Error saving user settings:", error);
      toast({ title: "Error", description: "Failed to save settings.", variant: "destructive" });
    }
  }, [firebaseUser?.uid, formData, updateUser, toast]);

  return {
    userData: liveUserData, 
    formData, 
    setFormData,
    isEditing,
    setIsEditing,
    isLoading: isFirebaseLoading,
    saveChanges,
    toast,
  };
};
